export type Screen = "chat" | "recent" | "details";

export interface AgentPublic {
  id: string;
  name: string;
  description?: string;

  avatar?: string; // dashboard avatar
  widgetAvatar?: string; // widget-specific avatar

  primaryColor: string; // header / accents
  textColor: string; // on primary
  chatBubbleColor: string;

  // New fields from API
  appearance: "LIGHT" | "DARK" | "AUTO" | null;
  headerBackgroundColor?: string | null;
  headerTextColor?: string | null;
  accentColor?: string | null;
  launcherIcon?: string | null;
  widgetPosition?: "BOTTOM_RIGHT" | "BOTTOM_LEFT" | null;
  initialMessages?: string[] | null;
  suggestedMessages?: string[] | null;

  websiteIntegrationEnabled: boolean;
  allowedDomains: string[];

  user?: {
    subscription?: {
      status: "ACTIVE" | "PAST_DUE" | "CANCELED" | "TRIALING";
      plan?: string | null;
      interval?: string | null;
      currentPeriodEnd?: string | null;
    };
  };
}

export interface ConversationSummary {
  id: string;
  title: string;
  updatedAt: string;
  status?: "OPEN" | "CLOSED" | "ESCALATED";
}

export type MessageRole = "USER" | "AGENT" | "SYSTEM";

export type SchedulingProvider = "CALENDLY" | "CALCOM";

export interface SchedulingSlot {
  start_time: string;
  end_time: string;
  scheduling_url: string;
}

export interface ChatMessage {
  id: string;
  role: MessageRole;
  content: string;
  createdAt: number;
  status?: "sending" | "sent" | "error";
  schedulingSlots?: SchedulingSlot[];
  schedulingProvider?: SchedulingProvider;
  bookingStatus?: "idle" | "booking" | "confirmed" | "error";
  bookingError?: string;
}

export type WidgetError = {
  kind: "init" | "conversation" | "offline" | "stream";
  title: string;
  description: string;
  actionLabel?: string;
  recoverable?: boolean;
};

export interface WidgetState {
  open: boolean;
  screen: Screen;
  loading: boolean;

  agent: AgentPublic | undefined;

  visitorId: string;
  conversationId: string | undefined;
  conversationStatus?: "OPEN" | "CLOSED" | "ESCALATED" | undefined;

  conversations: ConversationSummary[];
  messages: ChatMessage[];

  input: string;
  sending: boolean;

  streaming:
    | {
        active: boolean;
        messageId: string;
        content: string;
      }
    | undefined;

  error: WidgetError | undefined;

  showSuggestions: boolean;
  showInitialMessage: boolean;

  online: boolean;

  escalated?: boolean;

  streamStatus?: "idle" | "connecting" | "streaming" | "reconnecting";
}

export const initialState = (
  visitorId: string,
  conversationId: string | undefined,
): WidgetState => ({
  open: false,
  screen: "chat",
  loading: true,

  agent: undefined,

  visitorId,
  conversationId,

  conversations: [],
  messages: [],

  input: "",
  sending: false,

  streaming: undefined,
  error: undefined,
  showSuggestions: false,
  showInitialMessage: false,

  online: navigator.onLine,
  escalated: false,

  streamStatus: "idle",
});
